import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Crop, Trash2, Upload, X } from 'lucide-react';
import { IMAGE_MAX_BYTES } from '../../../../packages/shared/src/image-submissions.js';
import { ImageFramer, type FramerFragrance } from './ImageFramer.js';
import type { ImageCrop } from '../../../../packages/shared/src/image-crop.js';

type Save = <T>(url: string, method: string, body: unknown) => Promise<T>;
/**
 * Add, replace or remove a fragrance's picture in one overlay. Uploading attaches the picture straight
 * away; the description is written from the fragrance's name and licensing details are optional.
 * Framing is stored beside the untouched original, so "Adjust image" can reframe it at any time.
 */
export function AdminImageActions({ fragrance, mediaId, save, onChanged, disabled = false, onBusy, triggerSlot }: {
  fragrance: { id: string } & FramerFragrance; mediaId: string; save: Save; onChanged: (mediaId: string | null) => void;
  disabled?: boolean; onBusy?: (busy: boolean) => void; triggerSlot?: HTMLElement | null;
}) {
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null), heading = useRef<HTMLHeadingElement>(null);
  const [mode, setMode] = useState<'' | 'upload' | 'adjust' | 'remove'>('');
  const [file, setFile] = useState<File | null>(null), [preview, setPreview] = useState(''), [dragging, setDragging] = useState(false);
  const [source, setSource] = useState(''), [rights, setRights] = useState('');
  const [crop, setCrop] = useState<ImageCrop | null>(null), [loadingCrop, setLoadingCrop] = useState(false), [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  useEffect(() => {
    if (!file) { setPreview(''); return; }
    const url = URL.createObjectURL(file); setPreview(url); return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (mode && !element.open) { element.showModal(); heading.current?.focus(); }
    else if (!mode && element.open) element.close();
  }, [mode]);
  function begin(next: 'upload' | 'adjust' | 'remove') {
    setFile(null); setSource(''); setRights(''); setError(''); setCrop(null); setMode(next);
    if (next !== 'adjust') return;
    // Start from the saved framing; the editor itself works on the stored original.
    setLoadingCrop(true); setVersion(Date.now());
    save<{ crop: ImageCrop | null }>('/api/v1/admin/media/' + encodeURIComponent(mediaId), 'GET', undefined)
      .then(row => setCrop(row.crop ?? null)).catch(cause => setError((cause as Error).message)).finally(() => setLoadingCrop(false));
  }
  function close() { if (!busy) setMode(''); }
  function choose(next: File | null | undefined) {
    setError('');
    if (!next) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(next.type)) { setError('Choose a JPEG, PNG or WebP picture.'); return; }
    if (next.size > IMAGE_MAX_BYTES) { setError('Choose a picture smaller than 8 MB.'); return; }
    setFile(next); setCrop(null);
  }
  async function run(action: () => Promise<string | null>) {
    setBusy(true); onBusy?.(true); setError('');
    try { const next = await action(); setMode(''); onChanged(next); }
    catch (cause) { setError((cause as Error).message); }
    finally { setBusy(false); onBusy?.(false); }
  }
  function upload(event: FormEvent) {
    event.preventDefault();
    if (!file) { setError('Choose a picture first.'); return; }
    void run(async () => {
      const form = new FormData(); form.set('image', file);
      if (source.trim()) form.set('sourceUrl', source.trim());
      if (rights.trim()) form.set('rightsBasis', rights.trim());
      if (crop) form.set('crop', JSON.stringify(crop));
      return (await save<{ id: string }>('/api/v1/admin/fragrances/' + fragrance.id + '/image', 'POST', form)).id;
    });
  }
  function adjust(event: FormEvent) {
    event.preventDefault();
    void run(async () => { await save('/api/v1/admin/media/' + encodeURIComponent(mediaId) + '/crop', 'PUT', { crop }); return mediaId; });
  }
  const remove = () => void run(async () => { await save('/api/v1/admin/fragrances/' + fragrance.id + '/image', 'PUT', { mediaId: null }); return null; });
  const verb = mediaId ? 'Replace' : 'Add';
  const triggers = <div className="panel-actions">
    <button type="button" className="button secondary" disabled={disabled || busy} onClick={() => begin('upload')}>{verb} image</button>
    {mediaId && <button type="button" className="button secondary" disabled={disabled || busy} onClick={() => begin('adjust')}><Crop size={15} aria-hidden /> Adjust image</button>}
    {mediaId && <button type="button" className="button secondary" disabled={disabled || busy} onClick={() => begin('remove')}>Remove image</button>}
  </div>;
  return <>
    {triggerSlot ? createPortal(triggers, triggerSlot) : triggers}
    <dialog ref={dialog} className={'modal-dialog admin-image-dialog' + (mode === 'adjust' || (mode === 'upload' && preview) ? ' is-framing' : '')} aria-labelledby={id + '-title'}
      onClose={() => setMode('')} onCancel={event => { if (busy) event.preventDefault(); }}
      onClick={event => { if (event.target === dialog.current) close(); }}>
      <div className="modal-dialog-panel">
        <header className="modal-dialog-header">
          <h2 id={id + '-title'} ref={heading} tabIndex={-1}>{mode === 'remove' ? 'Remove this image?' : mode === 'adjust' ? 'Adjust image for ' + fragrance.name : verb + ' image for ' + fragrance.name}</h2>
          <button type="button" className="modal-close" onClick={close} disabled={busy} aria-label="Close"><X size={18} aria-hidden /></button>
        </header>
        {mode === 'remove' ? <>
          <p className="modal-dialog-intro">{fragrance.name} will show without a picture until you add a new one.</p>
          {error && <p className="error-message" role="alert">{error}</p>}
          <div className="modal-dialog-actions">
            <button type="button" className="modal-cancel" onClick={close} disabled={busy}>Cancel</button>
            <button type="button" className="button" onClick={remove} disabled={busy}><Trash2 size={16} aria-hidden />{busy ? 'Removing…' : 'Remove image'}</button>
          </div>
        </> : mode === 'adjust' ? <form onSubmit={adjust} noValidate>
          <p className="framer-help">Drag to move, scroll or use the slider to zoom. The original is kept, so you can change this again later.</p>
          {loadingCrop ? <p className="framer-loading" role="status">Loading the current framing…</p>
            : <ImageFramer src={'/media/' + encodeURIComponent(mediaId) + '?size=original&v=' + version} crop={crop} onChange={setCrop} fragrance={fragrance} disabled={busy} />}
          {error && <p className="error-message" role="alert">{error}</p>}
          <div className="modal-dialog-actions">
            <button type="button" className="modal-cancel" onClick={close} disabled={busy}>Cancel</button>
            <button className="button" disabled={busy || loadingCrop}><Crop size={16} aria-hidden />{busy ? 'Saving…' : 'Save framing'}</button>
          </div>
        </form> : <form onSubmit={upload} noValidate>
          <label className={'submission-drop' + (dragging ? ' dragging' : '') + (preview ? ' has-preview is-compact' : '')}
            onDragOver={event => { event.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
            onDrop={event => { event.preventDefault(); setDragging(false); choose(event.dataTransfer.files[0]); }}>
            <input className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} aria-describedby={id + '-help'} onChange={event => choose(event.target.files?.[0])} />
            {preview ? <>
              <span className="submission-drop-caption"><strong>{file?.name}</strong><span>Choose a different picture</span></span>
            </> : <>
              <span className="submission-drop-icon" aria-hidden="true"><Upload size={20} /></span>
              <strong>Choose a picture</strong>
              <span>or drag it here</span>
            </>}
          </label>
          {preview && <ImageFramer src={preview} crop={crop} onChange={setCrop} fragrance={fragrance} disabled={busy} />}
          <p id={id + '-help'} className="submission-help">JPEG, PNG or WebP · up to 8 MB. It goes live as soon as you {verb.toLowerCase()} it{preview ? ', framed as the card preview shows' : ''}. You can adjust the framing later.</p>
          <details className="admin-image-licensing">
            <summary>Licensing details <span>(optional)</span></summary>
            <label>Source page<input type="url" inputMode="url" placeholder="https://" value={source} disabled={busy} onChange={event => setSource(event.target.value)} /></label>
            <label>Licence or permission notes<textarea rows={3} value={rights} disabled={busy} onChange={event => setRights(event.target.value)} /></label>
          </details>
          {error && <p className="error-message" role="alert">{error}</p>}
          <div className="modal-dialog-actions">
            <button type="button" className="modal-cancel" onClick={close} disabled={busy}>Cancel</button>
            <button className="button" disabled={busy || !file}><Upload size={16} aria-hidden />{busy ? 'Uploading…' : verb + ' image'}</button>
          </div>
        </form>}
      </div>
    </dialog>
  </>;
}
